import {liveModeConstants} from "../../../constants/liveModeConstants"
import {messengerConstants} from "../../../constants/messengerConstants"
import {apiConstants} from "../../../constants/apiConstants"
import {liveServiceV2} from "../../../services/liveServiceV2"
import {localDay, newLiveEntryKey} from "../../reducers/liveMode/liveMemory"

export function setIsLiveModeEnabled(newState, liveSessionId) {
    if (newState)
        return {
            type: liveModeConstants.IS_LIVE_MODE_CHANGE,
            newState,
            liveSessionId,
            enteredOn: localDay(new Date()),
            entryKey: newLiveEntryKey(),
        }
    else return { type: liveModeConstants.RESET_LIVE_MODE }
}

export function setIsTyping(newState) {
    return { type: liveModeConstants.IS_TYPING, newState }
}

export function setTypingUser(newState) {
    return { type: liveModeConstants.TYPING_USER, newState }
}

export function setLiveParticipants(newState) {
    return { type: liveModeConstants.LIVE_PARTICIPANTS, newState }
}

export function addParticipant(newState) {
    return { type: liveModeConstants.ADD_PARTICIPANT, newState }
}

export function removeParticipant(newState) {
    return { type: liveModeConstants.REMOVE_PARTICIPANT, newState }
}

export function clearMembers() {
    return { type: liveModeConstants.CLEAR_MEMBERS }
}

export function showIncomingCall() {
    return { type: liveModeConstants.SHOW_INCOMING_CALL }
}

export function hideIncomingCall() {
    return { type: liveModeConstants.HIDE_INCOMING_CALL }
}

export function setInCallUsers(newState) {
    return { type: liveModeConstants.IN_CALL_USERS, newState }
}

export function addInCallUser(newState) {
    return { type: liveModeConstants.ADD_IN_CALL_USER, newState }
}

export function removeInCallUsers(newState) {
    return { type: liveModeConstants.REMOVE_IN_CALL_USERS, newState }
}

export function newLiveSessionLog(newState) {
    return { type: liveModeConstants.NEW_LIVE_SESSION_LOG, newState }
}

export function setTimesSinceLastCall(newState, calculatedAt = null) {
    return {
        type: liveModeConstants.SET_TIMES_SINCE_LAST_CALL,
        newState,
        calculatedAt: calculatedAt || new Date().toISOString(),
    }
}

export function setActiveTime(newState, calculatedAt = null) {
    return {
        type: liveModeConstants.SET_ACTIVE_TIME,
        newState,
        calculatedAt: calculatedAt || new Date().toISOString(),
    }
}

export function setRaisedHands(newState) {
    return { type: liveModeConstants.RAISED_HANDS, newState }
}

export function addRaisedHand(newState) {
    return { type: liveModeConstants.ADD_RAISED_HAND, newState }
}

export function removeRaisedHand(newState) {
    return { type: liveModeConstants.REMOVE_RAISED_HAND, newState }
}

export function setPhoneRinging(newState) {
    return { type: liveModeConstants.PHONE_RINGING, newState }
}

export function resetPhoneRinging() {
    return { type: liveModeConstants.RESET_PHONE_RINGING }
}

export function addPhoneRinging(newState) {
    return { type: liveModeConstants.ADD_PHONE_RINGING, newState }
}

export function removePhoneRinging(newState) {
    return { type: liveModeConstants.REMOVE_PHONE_RINGING, newState }
}

export function setCallPortal(newState, mode) {
    return { type: liveModeConstants.SET_CALL_PORTAL, newState, mode }
}

export function addMessage(newState) {
    return { type: liveModeConstants.SET_MESSAGES, newState }
}

export function resetLiveMessagesCount(threadId) {
    return { type: messengerConstants.RESET_LIVE_MESSAGES_COUNT, threadId }
}

export function playASound(sound) {
    return { type: liveModeConstants.PLAY_SOUND, sound }
}

export function setActivityFinalizedCount(sessionId, studentId) {
    return { type: liveModeConstants.ACTIVITY_FINALIZED, sessionId, studentId }
}

export function setRecentFinalizedActivity({ studentId, activityId, finishedAt }) {
    return {
        type: liveModeConstants.SET_RECENT_FINALIZED_ACTIVITY,
        studentId,
        activityId,
        finishedAt,
    }
}

export function clearRecentFinalizedActivity(studentId) {
    return {
        type: liveModeConstants.CLEAR_RECENT_FINALIZED_ACTIVITY,
        studentId,
    }
}

// Tutor-side signal feeding the events panel: a student in a group session
// self-evaluated and picked their next activity on their own (auto-mode).
// One signal carries both pieces of info — the self-eval and the chosen
// activity (openable via the card's "Voir l'énoncé" CTA).
export function setAutoModeStartedActivity({ studentId, evaluation, activityId }) {
    return {
        type: liveModeConstants.SET_AUTO_MODE_STARTED_ACTIVITY,
        studentId,
        evaluation,
        activityId,
    }
}

export function clearAutoModeStartedActivity(studentId) {
    return {
        type: liveModeConstants.CLEAR_AUTO_MODE_STARTED_ACTIVITY,
        studentId,
    }
}

// Guards against a second concurrent toggle for the same student (e.g. a
// double-click on the tile volume button, which has no debounce). Two
// interleaved disable/enable sequences are exactly the both-unmuted /
// both-muted server-side race the atomic mutex exists to prevent.
const pinInFlight = new Set()

// Dialogue mutex enforced atomically: two parallel setSoundMode dispatches
// can race server-side and momentarily leave both students in Dialogue (audio
// collision) or none (tutor goes silent). Awaiting disable → enable serializes
// the toggle and dispatches the matching _REQUEST/_SUCCESS/_FAILURE so the api
// + liveMode reducers (loading flag, soundModeIds, raisedHands) stay in sync
// without the apiRequest pipeline.
export function pinStudentInDialogueAtomic({
    liveSessionId,
    studentId,
    otherStudentsInDialogue = [],
}) {
    return async (dispatch) => {
        const flightKey = `${liveSessionId}-${studentId}`
        if (pinInFlight.has(flightKey)) {
            return { ok: false, inFlight: true }
        }
        pinInFlight.add(flightKey)

        const dispatchSoundModeSuccess = (response) => {
            dispatch({
                type:
                    liveServiceV2.setSoundModeV2.name + apiConstants._SUCCESS,
                data: response?.data?.data,
                all_data: response?.data,
            })
        }

        // Surface the request flag so the tile button shows its spinner and
        // blocks re-clicks while the multi-step sequence is in flight.
        dispatch({
            type: liveServiceV2.setSoundModeV2.name + apiConstants._REQUEST,
            data: { user_ids: [studentId] },
        })

        try {
            // Disable the previously-in-Dialogue students first so there is
            // never a moment where two students are unmuted at once.
            if (otherStudentsInDialogue.length > 0) {
                const disableRes = await liveServiceV2.setSoundModeV2(
                    { id: liveSessionId },
                    { enabled: false, user_ids: otherStudentsInDialogue },
                )
                dispatchSoundModeSuccess(disableRes)
            }

            let enableRes
            try {
                enableRes = await liveServiceV2.setSoundModeV2(
                    { id: liveSessionId },
                    { enabled: true, user_ids: [studentId] },
                )
            } catch (enableErr) {
                // Enable failed AFTER others were disabled → the tutor would be
                // left talking to nobody. Compensate by restoring the
                // previously-in-Dialogue students before surfacing the error.
                if (otherStudentsInDialogue.length > 0) {
                    try {
                        const restoreRes = await liveServiceV2.setSoundModeV2(
                            { id: liveSessionId },
                            {
                                enabled: true,
                                user_ids: otherStudentsInDialogue,
                            },
                        )
                        dispatchSoundModeSuccess(restoreRes)
                    } catch (restoreErr) {
                        // best-effort restore; fall through to the failure path
                    }
                }
                throw enableErr
            }
            dispatchSoundModeSuccess(enableRes)

            // Lower the now-in-Dialogue student's hand. A failure here must NOT
            // fail the whole toggle (sound mode already switched) — just keep
            // raisedHands in sync when it succeeds; otherwise it reconciles on
            // the next getCall sync.
            try {
                const lowerRes = await liveServiceV2.lowerHandsV2(
                    { id: liveSessionId },
                    { user_ids: [studentId] },
                )
                dispatch({
                    type:
                        liveServiceV2.lowerHandsV2.name +
                        apiConstants._SUCCESS,
                    data: lowerRes?.data?.data,
                    all_data: lowerRes?.data,
                })
            } catch (lowerErr) {
                // ignore — non-critical, reconciled on next sync
            }

            return { ok: true }
        } catch (err) {
            dispatch({
                type: liveServiceV2.setSoundModeV2.name + apiConstants._FAILURE,
                error: err?.response?.data ?? err,
            })
            return {
                ok: false,
                error: err,
                message: err?.response?.data?.message ?? null,
            }
        } finally {
            pinInFlight.delete(flightKey)
        }
    }
}

export function setIssoundMode(newState, userId) {
    return { type: liveModeConstants.SOUND_MODE, newState, userId }
}

/**
 * Le micro et la caméra choisis pendant le test d'entrée.
 *
 * Ils ne servaient qu'au test : la séance rouvrait ensuite le périphérique par
 * défaut du système, et l'élève devait refaire son choix une fois dedans. On
 * lui demandait de trouver le bon micro, puis on jetait sa réponse.
 *
 * @param {{audioinput?: string|null, videoinput?: string|null}} devices
 */
export function setPrecallDevices(devices) {
    return { type: liveModeConstants.PRECALL_DEVICES, devices }
}

export function setIsOutOfApp(newState, userId) {
    return { type: liveModeConstants.OUT_OF_APP, newState, userId }
}

export function setActivityCameraOn(newState, userId) {
    return { type: liveModeConstants.ACTIVITY_CAMERA_ON, newState, userId }
}

export function setVoiceRecordingIds(newState, userId) {
    return {
        type: liveModeConstants.VOICE_RECORDING_ACTIVITY,
        newState,
        userId,
    }
}

export function setCallKeepUUID(uuid) {
    return { type: liveModeConstants.SET_CALL_KEEP_UUID, uuid }
}

export function setCallKeepOn(newStatus) {
    return { type: liveModeConstants.SET_CALLKEEP_ON, newStatus }
}

export function setHasPhonePermission(newStatus) {
    return { type: liveModeConstants.SET_HAS_PHONE_PERMISSION, newStatus }
}

/** `isCallKeepOn` et `hasPhonePermission` écrits ensemble : la salle ne voit jamais l'un sans l'autre. */
export function setCallState({ isCallKeepOn, hasPhonePermission }) {
    return { type: liveModeConstants.SET_CALL_STATE, isCallKeepOn, hasPhonePermission }
}

export const setPoorConnectionQualityIds = (ids) => ({
    type: liveModeConstants.SET_POOR_CONNECTION_IDS,
    payload: ids,
})

export const addPoorConnectionId = (userId) => ({
    type: liveModeConstants.ADD_POOR_CONNECTION_ID,
    userId,
})

export const removePoorConnectionId = (userId) => ({
    type: liveModeConstants.REMOVE_POOR_CONNECTION_ID,
    userId,
})

export const setIsLocalPoorConnection = (value) => ({
    type: liveModeConstants.SET_IS_LOCAL_POOR_CONNECTION,
    value,
})

export const updateAgentState = (actionType, data) => ({
    type: liveModeConstants.UPDATE_AGENT_STATE,
    payload: {
        actionType,
        data,
    },
})

export const updateConversation = (actionType, data) => ({
    type: liveModeConstants.UPDATE_CONVERSATION,
    payload: {
        actionType,
        data,
    },
})

// count of the completed conversations not read of each student in the summaries modal
export const setAgentNotifCount = (liveSessionId, studentId, count) =>
    updateAgentState("SET_NOTIF_COUNT", { liveSessionId, studentId, count })

// ids of students that have ongoing conversations
export const setOngoingCheckpointIds = (studentIds, operation = "add") =>
    updateAgentState("SET_ACTIVE_CONVERSATIONS", { studentIds, operation })

// conversations that are completed not showed yet in the popup
export const setCompletedConversation = (
    liveSessionId,
    studentId,
    conversation
) =>
    updateConversation("ADD_CONVERSATION", {
        liveSessionId,
        studentId,
        conversation,
        timestamp: Date.now(),
        read: false,
    })

export const markConversationAsRead = (
    liveSessionId,
    studentId,
    conversationId
) =>
    updateConversation("MARK_READ", {
        liveSessionId,
        studentId,
        conversationId,
    })

export function setLastPopupTime(userId, sessionId, timestamp) {
    return {
        type: liveModeConstants.SET_LAST_POPUP_TIME,
        payload: {
            userId,
            sessionId,
            timestamp,
        },
    }
}

export function setShowRaiseHandCheckpoint(userId, show) {
    return {
        type: liveModeConstants.SET_SHOW_RAISE_HAND_CHECKPOINT,
        payload: {
            userId,
            show,
        },
    }
}

export const setNewTodoActivity = (activity, forceFocus = false) => {
    return {
        type: liveModeConstants.SET_NEW_TODO_ACTIVITY,
        activity,
        forceFocus,
    }
}

export function setCurrentStudentActivity(studentId, activity) {
    return {
        type: liveModeConstants.SET_CURRENT_STUDENT_ACTIVITY,
        studentId,
        activity,
    }
}

// The activity each student device has in front of the student: its
// `activity_id`, or null when `activity_on_screen` says "0" (the new student
// space: hidden under the visio on a phone, or behind a finished activity
// they re-read). `seen`: { [participant identity]: activityId | null };
// an undefined value forgets a device that left; `replace` rebuilds the
// whole map from the devices in the room.
export function setSeenStudentActivity(seen, replace = false) {
    return {
        type: liveModeConstants.SET_SEEN_STUDENT_ACTIVITY,
        seen,
        replace,
    }
}

// Explicit "open this activity in focus for this student" request from the
// tutor (events-panel CTA). Distinct from setCurrentStudentActivity, which the
// LiveKit attribute sync also writes passively — that passive write makes the
// CTA a no-op when the student is already on the activity. The reducer bumps a
// requestId so the focus layout re-selects on every click even if the id is
// unchanged.
export function requestStudentActivityFocus(studentId, activityId) {
    return {
        type: liveModeConstants.REQUEST_STUDENT_ACTIVITY_FOCUS,
        studentId,
        activityId,
    }
}

export function setIsGrayedOut(newState) {
    return { type: liveModeConstants.SET_IS_GRAYED_OUT, newState }
}

export function setIsAgentModalVisible(newState) {
    return { type: liveModeConstants.SET_IS_AGENT_MODAL_VISIBLE, newState }
}

export function setMiniMode(newState) {
    return { type: liveModeConstants.SET_MINI_MODE, newState }
}

export function setFocusedMode(newState) {
    return { type: liveModeConstants.SET_FOCUSED_MODE, newState }
}

export const setSuggestedActivity = (liveSessionId, activityId) => {
    return {
        type: liveModeConstants.SET_SUGGESTED_ACTIVITY,
        liveSessionId,
        activityId,
    }
}

export const updateParticipantActiveTime = (
    participantId,
    activeTime,
    calculatedAt = null
) => {
    return {
        type: liveModeConstants.UPDATE_PARTICIPANT_ACTIVE_TIME,
        participantId,
        activeTime,
        calculatedAt: calculatedAt || new Date().toISOString(),
    }
}

export const updateParticipantTimesSinceLastCall = (
    participantId,
    timesSinceLastCall,
    calculatedAt = null
) => {
    return {
        type: liveModeConstants.UPDATE_PARTICIPANT_TIMES_SINCE_LAST_CALL,
        participantId,
        timesSinceLastCall,
        calculatedAt: calculatedAt || new Date().toISOString(),
    }
}

export const setTutorEverPresent = (sessionKey) => {
    return {
        type: liveModeConstants.SET_TUTOR_EVER_PRESENT,
        sessionKey,
    }
}

export const cleanOldTutorPresence = () => {
    return {
        type: liveModeConstants.CLEAN_OLD_TUTOR_PRESENCE,
    }
}

// « J'ai compris » sur le pop-up de débordement du tuteur, pour une occurrence.
export const ackOverrun = (occurrenceId) => {
    return {
        type: liveModeConstants.ACK_TUTOR_OVERRUN,
        occurrenceId,
    }
}

export const setCheckpointConversation = (conversation) => {
    return {
        type: liveModeConstants.SET_CHECKPOINT_CONVERSATION,
        conversation,
    }
}

export const clearCheckpointConversation = () => {
    return {
        type: liveModeConstants.CLEAR_CHECKPOINT_CONVERSATION,
    }
}

export const setQuizProgress = (conversationId, answers, currentQuestionIndex) => {
    return {
        type: liveModeConstants.SET_QUIZ_PROGRESS,
        payload: {
            conversationId,
            answers,
            currentQuestionIndex,
        },
    }
}

export const clearQuizProgress = (conversationId) => {
    return {
        type: liveModeConstants.CLEAR_QUIZ_PROGRESS,
        payload: { conversationId },
    }
}

export function setLiveWebMobileBannerShown() {
    return { type: liveModeConstants.SET_LIVE_WEB_MOBILE_BANNER_SHOWN }
}

export function setLiveWebMobileBannerCollapsed(collapsed) {
    return {
        type: liveModeConstants.SET_LIVE_WEB_MOBILE_BANNER_COLLAPSED,
        collapsed,
    }
}

export function setLeftPanelCollapsed(collapsed) {
    return { type: liveModeConstants.SET_LEFT_PANEL_COLLAPSED, collapsed }
}

export function setRightPanelCollapsed(collapsed) {
    return { type: liveModeConstants.SET_RIGHT_PANEL_COLLAPSED, collapsed }
}

export function setFocusScreenShare(isFocus) {
    return { type: liveModeConstants.SET_FOCUS_SCREEN_SHARE, isFocus }
}

export function batchUpdateTimers(timesSinceLastCallUpdates, activeTimeUpdates, calculatedAt = null) {
    return {
        type: liveModeConstants.BATCH_UPDATE_TIMERS,
        timesSinceLastCallUpdates,
        activeTimeUpdates,
        calculatedAt: calculatedAt || new Date().toISOString(),
    }
}

// Auto mode (P-1292) — élève choisit seul son activité suivante en séance de groupe
export const openAutoModeEval = (sourceActivity) => ({
    type: liveModeConstants.AUTO_MODE_OPEN_EVAL,
    sourceActivity,
})

export const setAutoModeEval = (selectedEval) => ({
    type: liveModeConstants.AUTO_MODE_SET_EVAL,
    selectedEval,
})

export const addAutoModeProposal = (proposal) => ({
    type: liveModeConstants.AUTO_MODE_ADD_PROPOSAL,
    proposal,
})

export const setAutoModeLoading = (isLoading) => ({
    type: liveModeConstants.AUTO_MODE_SET_LOADING,
    isLoading,
})

export const setAutoModeError = (error) => ({
    type: liveModeConstants.AUTO_MODE_SET_ERROR,
    error,
})

export const closeAutoMode = () => ({
    type: liveModeConstants.AUTO_MODE_CLOSE,
})